import React from 'react'

import { useCurrentStatusStore } from 'store/currentStatusStore'
import { isEqualDate } from 'utils/convertFromLocalDate'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Navigation detection utilities
export const isPageReload = (): boolean => {
  const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
  if (navigationEntries.length > 0) {
    return navigationEntries[0].type === 'reload'
  }
  
  // Fallback for old browsers
  return performance.navigation?.type === performance.navigation?.TYPE_RELOAD
}

export const isFirstLoad = (): boolean => !sessionStorage.getItem('hasNavigated')

export const markAsNavigated = () => {
  sessionStorage.setItem('hasNavigated', 'true')
}

// Cache key generator
const generateCacheKey = (apiPath: string, params: Record<string, any> = {}): string => {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((result, key) => {
      const value = params[key]
      // Skip undefined, null, or empty string values
      if (value !== undefined && value !== null && value !== '') {
        return {
          ...result,
          [key]: value
        }
      }
      return result
    }, {} as Record<string, any>)
  
  return `${apiPath}:${JSON.stringify(sortedParams)}`
}

// Helper function to handle cache logic for enabled=false requests
const handleEnabledFalseCache = (
  apiPath: string,
  params: Record<string, any>,
  response: { data: any; result: any },
  hasValidParams: boolean,
  cacheKey: string,
  storeActions: { setCache: (key: string, data: any, systemDate?: string | null) => void; clearCache: (key?: string) => void }
) => {
  if (Object.keys(params).length > 0 && params.enabled === false) {
    const cacheKeyEnabled = generateCacheKey(apiPath, {enabled: true, companyId: params.companyId})
    
    if (hasValidParams) {
      // If there are filter params, clear both caches
      storeActions.clearCache(cacheKeyEnabled)
      storeActions.clearCache(cacheKey)
    }
    if (!hasValidParams && params.enabled === false) {
      // If no filter params, cache the enabled list
      const enabledList = (response.data as any).list.filter((item: any) => item.deleteFlg === false)
      const responseEnabled = {
        ...response,
        data: {
          ...response.data,
          list: enabledList,
          datacount: enabledList.length,
          maxcount: enabledList.length,
        },
      }
      storeActions.setCache(cacheKeyEnabled, responseEnabled, response.result?.systemDate)
      storeActions.setCache(cacheKey, response, response.result?.systemDate)
    }
  }
  // Cache the response if there are no filter params
  if (!hasValidParams) {
    storeActions.setCache(cacheKey, response, response.result?.systemDate)
  }
  useCurrentStatusStore.getState().setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
}

const clearCacheError = (
  apiPath: string,
  params: Record<string, any>,
  cacheKey: string,
  storeActions: {
    getCache: (key: string, currentSystemDate?: string | null, ttl?: number) => any | null
    clearCache: (key?: string) => void
  }
) => {
  const enabledKey = generateCacheKey(apiPath, { enabled: true, companyId: params.companyId })
  if (storeActions.getCache(cacheKey)) {
    storeActions.clearCache(cacheKey)
  }
  if (storeActions.getCache(enabledKey)) {
    storeActions.clearCache(enabledKey)
  }
}

interface CacheEntry {
  data: any
  timestamp: number
  systemDate: string | null
}

interface ApiCacheStore {
  // Cache data
  cache: Record<string, CacheEntry>
  
  // State tracking
  isPageReload: boolean
  setIsPageReload: (value: boolean) => void
  
  // Cache methods
  setCache: (key: string, data: any, systemDate?: string | null) => void
  getCache: (key: string, currentSystemDate?: string | null, ttl?: number) => any | null
  clearCache: (key?: string) => void
  clearAllCache: () => void
  
  // API wrapper
  callApiWithCache: <T>(
    apiPath: string,
    params: Record<string, any>,
    apiCall: () => Promise<{ data: T; result: any }>,
    systemdate?: Date | null,
    forceRefresh?: boolean,
    ttl?: number
  ) => Promise<{ data: T; result: any }>
}

export const useApiCacheStore = create<ApiCacheStore>()(
  persist(
    (set, get) => ({
      cache: {},
      isPageReload: false,
      
      setIsPageReload: (value) => set({ isPageReload: value }),
      
      setCache: (key, data, systemDate = null) => set((state) => ({
        cache: { 
          ...state.cache, 
          [key]: {
            data,
            timestamp: Date.now(),
            systemDate
          }
        }
      })),
      
      getCache: (key, currentSystemDate = null, ttl = 720 * 60 * 1000) => {
        const state = get()
        const entry = state.cache[key]
        
        if (!entry) return null
        
        // Check TTL
        if (Date.now() - entry.timestamp > ttl) {
          return null
        }
        
        // Check system date - if different date then cache invalid
        if (currentSystemDate && entry.systemDate && currentSystemDate !== entry.systemDate) {
          return null
        }
        
        return entry.data
      },
      
      clearCache: (key) => set((state) => {
        if (key) {
          const { [key]: removedEntry, ...remainingCache } = state.cache
          return { cache: remainingCache }
        }
        return { cache: {} }
      }),
      
      clearAllCache: () => set({ cache: {} }),

      callApiWithCache: async <T>(
        apiPath: string,
        fetchParams: Record<string, any>,
        apiCall: () => Promise<{ data: T; result: any }>,
        systemdate: Date | null = null,
        forceRefresh = false,
        ttl = 720 * 60 * 1000
      ) => {
        const params: Record<string, any> = {
          ...fetchParams,
          enabled: fetchParams.enabled ?? true
        }
        const state = get()
        const cacheKey = generateCacheKey(apiPath, params)
        const {companyId, enabled, ...objParams} = params
        const hasValidParams = Object.values(objParams).some(value => value !== undefined && value !== null && value !== '')
        // Will hold the latest successful API response for this call
        let response: { data: T; result: any } | null = null

        try {
          // If page reload or force refresh → always call API (no cache read)
          if (state.isPageReload || forceRefresh) {
            response = await apiCall()
            if (response.result.status !== 'success') {
              clearCacheError(apiPath, params, cacheKey, { getCache: get().getCache, clearCache: get().clearCache })
              return response
            }
            handleEnabledFalseCache(apiPath, params, response as { data: any; result: any }, hasValidParams, cacheKey, { setCache: get().setCache, clearCache: get().clearCache })
            return response
          }
          
          // Normal navigation: check cache before calling API
          const cachedData = get().getCache(cacheKey, params.systemDate, ttl)
          if (cachedData && (isEqualDate(cachedData.result?.systemDate, systemdate) || systemdate === null) && (!forceRefresh || !state.isPageReload)) {
            // Fake API call (return cached data wrapped in Promise)
            return await new Promise((resolve) => {
              setTimeout(() => resolve(cachedData), 50) // Fake delay short
            })
          }
          
          // If no cache or cache invalid → call API and then try to cache
          response = await apiCall()
          if (response.result.status !== 'success') {
            clearCacheError(apiPath, params, cacheKey, { getCache: get().getCache, clearCache: get().clearCache })
            return response
          }
          handleEnabledFalseCache(apiPath, params, response as { data: any; result: any }, hasValidParams, cacheKey, { setCache: get().setCache, clearCache: get().clearCache })
          return response
        } catch (error) {
          // If QuotaExceededError occurs when saving to localStorage:
          //  - Clear the cache of this API (cacheKey + enabledKey)
          //  - Do not throw, return the response to the UI (skip cache for this time)
          if (error instanceof DOMException && error.name === 'QuotaExceededError') {
            // Clear cache entries related to this key (current params)
            clearCacheError(apiPath, params, cacheKey, { getCache: get().getCache, clearCache: get().clearCache })

            if (response) {
              // API successful, only cache failed → return response
              return response
            }
            throw error
          }
          // Other errors keep original behavior
          throw error
        }
      }
    }),
    {
      name: 'api-cache-storage',
      version: 1
    }
  )
)

// Hook to detect F5/Ctrl+Shift+R
export const useRefreshHandler = () => {
  const { clearAllCache, setIsPageReload } = useApiCacheStore()
  
  React.useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // F5 or Ctrl+Shift+R
      if (e.key === 'F5' || (e.ctrlKey && e.shiftKey && e.key === 'R')) {
        clearAllCache()
        setIsPageReload(true)
      }
    }
    
    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [clearAllCache, setIsPageReload])
}
